/**
 * MenuItemInlineEditor — treść akordeonu rozwijana pod wierszem pozycji menu.
 *
 * Zawiera dwie sekcje:
 *  (a) Pola właściwości (label, icon, url, collapsed, moduleCode, itd.) —
 *      wyjęte z MenuItemPropertiesPanel jako osobna funkcja renderFields().
 *  (b) Przypisanie widoków (layout formularza + listy + przyciski builderów) —
 *      wyjęte z MenuItemViewAssignmentModal.
 *
 * Zapis właściwości jest natychmiastowy (controlled przez onChange). Przypisanie
 * formularza jest również natychmiastowe (onAssignForm wywołuje updateInTree).
 * Ciężkie buildery (formularz / lista) otwierane przez onDesignForm / onDesignList
 * / onCreateForm — bez modalu pośredniego.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type AdminMenuItem,
  DOCUMENT_TYPE_FILTER_FIELD,
  type DocumentClass,
  type FilterConfig,
  type MenuItem,
  type ModuleTemplateEntityDefinition,
  type SortConfig,
} from '@saas/shared';
import {
  CheckCircle,
  Copy,
  Eye,
  Filter,
  ListChecks,
  Loader2,
  MonitorPlay,
  Pencil,
  Plus,
  SquarePen,
  Trash2,
} from 'lucide-react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { IconPicker } from '@/components/modules/IconPicker';
import { getKartotekaSlugForClass } from '@/lib/documents/class-kartoteka-slug';
import { useKartotekaDefinition } from '@/lib/kartoteki/kartoteka-definition';
import { parseMenuViewRef } from '@/lib/module-templates/template-layouts';

import { BaseFiltersEditor } from './BaseFiltersEditor';
import { BaseSortEditor } from './BaseSortEditor';

/**
 * Wyciąga slug encji (kartoteki) z moduleCode pozycji menu typu 'module'.
 * Zwraca null dla innych typów / gdy moduleCode nie pasuje do prefiksu szablonu.
 * Współdzielone przez edytor inline (sekcja widoków) i wiersz (badge kartoteki).
 */
export function resolveMenuItemEntitySlug(
  item: MenuItem | AdminMenuItem,
  templateCode?: string,
): string | null {
  return item.type === 'module' && templateCode && item.moduleCode?.startsWith(`${templateCode}_`)
    ? item.moduleCode.slice(templateCode.length + 1)
    : null;
}

/**
 * Rozwiązuje czytelną nazwę kartoteki (encji) dla pozycji menu typu 'module'.
 * Zwraca null gdy nie da się dopasować (inny typ, brak szablonu/encji).
 */
export function resolveMenuItemKartotekaName(
  item: MenuItem | AdminMenuItem,
  templateCode: string | undefined,
  entities: ModuleTemplateEntityDefinition[],
): string | null {
  const entitySlug = resolveMenuItemEntitySlug(item, templateCode);
  if (!entitySlug) return null;
  return entities.find((e) => e.slug === entitySlug)?.name ?? null;
}

/**
 * Etap 3, blok D (ITE) — czy klasa dokumentu dopuszcza zawężenie listy do wybranych
 * typów przez filtr stały `document_type_id` (multi-select w `BaseFiltersEditor`).
 * WYŁĄCZNIE `specjalne`/`internal` konsumują `documentTypeIds` na liście (bloki B/C) —
 * dla pozostałych 5 klas backend IGNORUJE ten filtr, więc oferowanie go w edytorze
 * byłoby cichym no-opem (zweryfikowane ograniczenie zadania, nie zgadywane tutaj).
 * Wyodrębnione jako czysta funkcja — pozwala przetestować regułę bez renderowania
 * całego edytora (ciężki render: query kartoteki, IconPicker, ConfirmDialog…).
 */
export function classSupportsDocumentTypeNarrowing(
  documentClass: DocumentClass | null | undefined,
): boolean {
  return documentClass === 'specjalne' || documentClass === 'internal';
}

export interface MenuItemInlineEditorProps {
  item: MenuItem | AdminMenuItem;
  onChange: (next: MenuItem | AdminMenuItem) => void;
  /**
   * i18n key prefix (np. 'admin.menuLayoutBuilder').
   * Default: 'tenant.menuLayoutBuilder' (back-compat).
   */
  i18nPrefix?: string;
  /**
   * Kod szablonu — wymagany do rozpoznania entitySlug z moduleCode.
   * Gdy undefined, sekcja widoków jest ukryta.
   */
  templateCode?: string;
  /**
   * Nadpisanie rozpoznawania encji (scope tenanta, 2026-07-02): pozycje menu
   * klienta adresują kartoteki slugiem BEZ prefiksu szablonu (moduleCode = slug).
   * Gdy podane — wygrywa z logiką prefiksu `templateCode_`.
   */
  resolveEntitySlug?: (item: MenuItem | AdminMenuItem) => string | null;
  /** Encje szablonu (źródło nazwanych layoutów). */
  entities?: ModuleTemplateEntityDefinition[];
  /** Przypisz layout formularza: viewRef 'entitySlug:viewSlug' lub null = domyślny. */
  onAssignForm?: (viewRef: string | null) => void;
  /** Otwórz builder formularza dla layoutu o danym slugu (null = domyślny). */
  onDesignForm?: (viewSlug: string | null) => void;
  /** Otwórz builder listy dla layoutu o danym slugu (null = domyślny/inline). */
  onDesignList?: (viewSlug: string | null) => void;
  /** Przypisz nazwany layout listy: listRef 'entitySlug:viewSlug' lub null = domyślny. */
  onAssignList?: (listRef: string | null) => void;
  /**
   * Utwórz nowy nazwany layout formularza. Zwrotka jest ignorowana przez edytor —
   * hub zwraca slug (sync, lokalna mutacja szablonu), tenant nic (async POST +
   * invalidacja źródła; wiersz pojawia się po odświeżeniu encji).
   */
  onCreateForm?: (name: string) => unknown;
  /**
   * Duplikuj istniejący layout formularza (null = domyślny) z podaną nazwą.
   * Nazwa jest zawsze podana przez użytkownika (z inputu create). Zwrotka ignorowana.
   */
  onDuplicateForm?: (sourceSlug: string | null, name: string) => unknown;
  /** Utwórz nowy nazwany layout listy. Zwrotka ignorowana (jak onCreateForm). */
  onCreateList?: (name: string) => unknown;
  /**
   * Duplikuj istniejący layout listy (null = domyślny) z podaną nazwą.
   * Nazwa jest zawsze podana przez użytkownika (z inputu create). Zwrotka ignorowana.
   */
  onDuplicateList?: (sourceSlug: string | null, name: string) => unknown;
  /**
   * Otwórz builder formularza w trybie tylko do odczytu (Podgląd).
   * Gdy podany, domyślny layout formularza wyświetla ten handler zamiast edytowalnego onDesignForm.
   */
  onPreviewForm?: (viewSlug: string | null) => void;
  /**
   * Otwórz builder listy w trybie tylko do odczytu (Podgląd).
   * Gdy podany, domyślny layout listy wyświetla ten handler zamiast edytowalnego onDesignList.
   */
  onPreviewList?: (viewSlug: string | null) => void;
  /** Usuń nazwany (non-default) layout formularza o danym slugu. */
  onDeleteForm?: (viewSlug: string) => void;
  /** Usuń nazwany (non-default) layout listy o danym slugu. */
  onDeleteList?: (viewSlug: string) => void;
  /**
   * Inline rename nazwanego layoutu formularza (ołówek na wierszu, 2026-07-03).
   * Walidacja unikalności nazwy robi edytor; zwrotka ignorowana (tenant: async PUT).
   */
  onRenameForm?: (viewSlug: string, name: string) => unknown;
  /** Inline rename nazwanego widoku listy (jak onRenameForm). */
  onRenameList?: (viewSlug: string, name: string) => unknown;
  /**
   * Podgląd „na żywo" AKTYWNEGO formularza pozycji na danych przykładowych (makieta
   * „jak klient zobaczy formularz"). Dostępny tylko dla kartotek `new_table`.
   */
  onLivePreviewForm?: () => void;
  /**
   * Podgląd „na żywo" AKTYWNEGO widoku listy pozycji na danych przykładowych.
   * Dostępny tylko dla kartotek `new_table`.
   */
  onLivePreviewList?: () => void;
  /**
   * Źródło typów dla pickera „Typ dokumentu" w filtrach stałych: 'ite' = katalog globalny
   * (kreator menu szablonu), 'tenant' = scalona lista typów tenanta (kreator menu klienta).
   * Default 'ite' (zgodność wsteczna — dotychczasowy jedyny caller to strona ITE).
   */
  documentTypePickerScope?: 'ite' | 'tenant';
}

export function MenuItemInlineEditor({
  item,
  onChange,
  i18nPrefix = 'tenant.menuLayoutBuilder',
  templateCode,
  resolveEntitySlug,
  entities = [],
  onAssignForm,
  onDesignForm,
  onDesignList,
  onAssignList,
  onCreateForm,
  onDuplicateForm,
  onCreateList,
  onDuplicateList,
  onPreviewForm,
  onPreviewList,
  onDeleteForm,
  onDeleteList,
  onRenameForm,
  onRenameList,
  onLivePreviewForm,
  onLivePreviewList,
  documentTypePickerScope = 'ite',
}: MenuItemInlineEditorProps) {
  const { t } = useTranslation();

  // ── widoki (type='module' z templateCode; tenant: custom resolver) ────────
  const entitySlug = resolveEntitySlug
    ? resolveEntitySlug(item)
    : resolveMenuItemEntitySlug(item, templateCode);
  const entity = entitySlug ? (entities.find((e) => e.slug === entitySlug) ?? null) : null;

  // ── Filtry stałe: źródło pól (hook ZAWSZE wywoływany — reguła hooks) ────
  // Dla encji reference_to_system_entity: query enabled gdy slug dostępny.
  // Dla encji document_class: query enabled gdy klasa rozpoznana (slug kartoteki
  // klasy) — `typeId` (documentGlobalTypeId) dociąga bramkowanie per typ (ADR-0085),
  // tak jak przy widoku TYPU dokumentu gdzie indziej w panelu ITE.
  // Dla other: query disabled (slug=undefined).
  const isSystemRef = entity?.ddlStrategy === 'reference_to_system_entity';
  const isDocumentClass = entity?.ddlStrategy === 'document_class';
  const documentClassKartotekaSlug =
    isDocumentClass && entity?.documentClass
      ? getKartotekaSlugForClass(entity.documentClass as DocumentClass)
      : undefined;
  const kartotekaSlug = isSystemRef
    ? entity?.referenceSystemModuleSlug
    : documentClassKartotekaSlug;
  const kartotekaTypeId = isDocumentClass ? entity?.documentGlobalTypeId : undefined;
  const kartotekaQuery = useKartotekaDefinition(kartotekaSlug, kartotekaTypeId);
  const kartotekaFields = kartotekaQuery.data?.fields ?? [];

  // Blok D — zawężenie listy do typów dokumentu, TYLKO dla klas które lista faktycznie
  // konsumuje (patrz classSupportsDocumentTypeNarrowing). `undefined` dla pozostałych klas/encji
  // — BaseFiltersEditor bez documentClass zachowuje się dokładnie jak przed blokiem D.
  //
  // Klasę czytamy WPROST z `entity.documentClass`, NIEZALEŻNIE od `ddlStrategy`: encje tenanta
  // z `useTenantKartotekaViews` mają `ddlStrategy:'new_table'`, ale niosą `documentClass` dla
  // kartotek dokumentów — dzięki temu picker typów działa też po stronie klienta (scope
  // `documentTypePickerScope` wybiera źródło typów: 'ite' = katalog globalny, 'tenant' =
  // scalona lista tenanta). Rozdzielenie klasy od strategii DDL jest istotne: `filterFields`
  // niżej dalej routuje ŹRÓDŁO PÓL po `isDocumentClass`, więc tenant (new_table) bierze
  // `entity.fields`, a nie fetch admin-scope (`useKartotekaDefinition`), który pod tenant auth by padł.
  const documentClassValue = entity?.documentClass as DocumentClass | undefined;
  const documentTypeNarrowingClass: DocumentClass | undefined = classSupportsDocumentTypeNarrowing(
    documentClassValue,
  )
    ? documentClassValue
    : undefined;

  // Pola dostępne do filtrów/sortowania:
  //   - reference_to_system_entity → z kartoteki systemowej
  //   - document_class → z puli pól klasy (kartoteka systemowa klasy + bramkowanie typu),
  //     z syntetycznym polem „Typ dokumentu" na początku dla specjalne/internal
  //   - new_table / reference_to_creator_entity → z entity.fields
  // Filtry stałe z menu stosują dziś tylko listy modułów własnych oraz dokumentów wewnętrznych
  // i specjalnych (`baseFiltersToHeaderFilters` w ModuleRecordsPage / InternalDocs / SpecialDocs).
  // Kartoteki systemowe (np. kontrahenci) i pozostałe klasy dokumentów ich nie czytają — tam
  // przycisk `@userId` dawałby złudzenie zawężenia listy, więc go nie pokazujemy.
  const baseFiltersApplyAtRuntime =
    !isSystemRef &&
    (!documentClassValue ||
      documentClassValue === 'internal' ||
      documentClassValue === 'specjalne');

  // `fieldType` niesiemy dalej — edytor filtrów stałych pokazuje po nim przycisk `@userId`.
  const filterFields: Array<{ id: string; name: string; fieldType?: string | null }> = (() => {
    const base =
      isSystemRef || isDocumentClass
        ? kartotekaFields.map((f) => ({ id: f.slug, name: f.name, fieldType: f.fieldType }))
        : (entity?.fields ?? []).map((f) => ({ id: f.slug, name: f.name, fieldType: f.type }));
    if (!documentTypeNarrowingClass) return base;
    return [
      {
        id: DOCUMENT_TYPE_FILTER_FIELD,
        name: t('admin.menuItemBaseFilters.documentTypeField', {
          defaultValue: 'Typ dokumentu',
        }),
      },
      ...base,
    ];
  })();

  // Zakładka aktywna w sekcji Filtry stałe
  const [baseFiltersTab, setBaseFiltersTab] = useState<'conditions' | 'sort'>('conditions');
  // Widoki renderowane jako wiersze (tylko nazwane, bez domyślnego).
  const formViews = entity ? entity.views.filter((v) => v.type === 'form' && !v.isDefault) : [];
  const listViews = entity ? entity.views.filter((v) => v.type === 'list' && !v.isDefault) : [];
  // Wszystkie widoki (łącznie z domyślnym) — używane wyłącznie do kolizji nazw.
  // Nowy layout nie może mieć nazwy identycznej z domyślnym ani z żadnym istniejącym.
  const allFormViews = entity ? entity.views.filter((v) => v.type === 'form') : [];
  const allListViews = entity ? entity.views.filter((v) => v.type === 'list') : [];

  // ── formularz ─────────────────────────────────────────────────────────────
  const assignedSlug = (() => {
    if (item.type !== 'module' || !item.viewRef || !entitySlug) return null;
    const parsed = parseMenuViewRef(item.viewRef);
    return parsed?.entitySlug === entitySlug ? parsed.viewSlug : null;
  })();

  const [selectedSlug, setSelectedSlug] = useState<string | null>(assignedSlug);
  const [showNewInput, setShowNewInput] = useState(false);
  const [newName, setNewName] = useState('');
  const [newNameError, setNewNameError] = useState('');
  /**
   * Chip „kopia od" dla formularzy. Gdy ustawiony (null = domyślny, string = slug widoku),
   * przycisk „Utwórz" w inpucie tworzy KOPIĘ (z seeded configiem) zamiast pustego layoutu.
   * undefined = brak chipa (tworzenie od zera). Kasowany po „Utwórz" lub anulowaniu inputu.
   */
  const [formDuplicateChip, setFormDuplicateChip] = useState<string | null | undefined>(undefined);

  // ── lista ──────────────────────────────────────────────────────────────────
  const assignedListSlug = (() => {
    const listRef = item.type === 'module' ? (item as MenuItem).listRef : undefined;
    if (!listRef || !entitySlug) return null;
    const parsed = parseMenuViewRef(listRef);
    return parsed?.entitySlug === entitySlug ? parsed.viewSlug : null;
  })();

  const [selectedListSlug, setSelectedListSlug] = useState<string | null>(assignedListSlug);
  const [showNewListInput, setShowNewListInput] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListNameError, setNewListNameError] = useState('');
  /**
   * Chip „kopia od" dla list. Analogia do formDuplicateChip.
   * undefined = brak chipa (tworzenie od zera).
   */
  const [listDuplicateChip, setListDuplicateChip] = useState<string | null | undefined>(undefined);

  // Stan „tworzę…" akcji layoutu (POST + refetch definicji ~1-2s) — trzyma input
  // otwarty ze spinnerem, żeby nowy/duplikowany layout nie doskakiwał bez feedbacku.
  const [busyForm, setBusyForm] = useState(false);
  const [busyList, setBusyList] = useState(false);

  const listIsCustom =
    item.type === 'module' || item.type === 'system_item'
      ? !!(item as MenuItem).view?.listConfig
      : false;

  const defaultLayoutName = t('admin.menuItemViews.defaultLayout', { defaultValue: 'Domyślny' });
  const customName = t('admin.menuLayoutBuilder.viewChips.custom', { defaultValue: 'Własny' });

  function handleAssignSlug(slug: string | null) {
    setSelectedSlug(slug);
    if (onAssignForm) {
      onAssignForm(slug && entitySlug ? `${entitySlug}:${slug}` : null);
    }
  }

  async function handleCreate() {
    if (busyForm) return;
    const name = newName.trim();
    if (!name) {
      setNewNameError(t('admin.menuItemViews.newNameRequired', { defaultValue: 'Podaj nazwę' }));
      return;
    }
    // Duplicate-name guard: case-insensitive, trimmed. Sprawdza ALL widoków form
    // (łącznie z domyślnym) — nowy layout nie może kolidować z żadną istniejącą nazwą.
    const nameLower = name.toLowerCase();
    const hasDuplicate = allFormViews.some((v) => v.name.trim().toLowerCase() === nameLower);
    if (hasDuplicate) {
      setNewNameError(
        t('admin.menuItemViews.duplicateName', {
          defaultValue: 'Widok o tej nazwie już istnieje.',
        }),
      );
      return;
    }
    // Chip = duplikuj ze źródła (formDuplicateChip !== undefined).
    // Brak chipa = tworzenie od zera.
    let action: unknown;
    if (formDuplicateChip !== undefined) {
      if (!onDuplicateForm) return;
      action = onDuplicateForm(formDuplicateChip, name);
    } else {
      if (!onCreateForm) return;
      action = onCreateForm(name);
    }
    // Trzymaj input+spinner do końca POST-a i refetchu definicji — inaczej nowy
    // layout doskakuje z ~2s opóźnieniem bez feedbacku (zwrotka może być Promise).
    setBusyForm(true);
    try {
      await Promise.resolve(action);
    } finally {
      setBusyForm(false);
      setShowNewInput(false);
      setNewName('');
      setNewNameError('');
      setFormDuplicateChip(undefined);
    }
  }

  function handleAssignListSlug(slug: string | null) {
    setSelectedListSlug(slug);
    if (onAssignList) {
      onAssignList(slug && entitySlug ? `${entitySlug}:${slug}` : null);
    }
  }

  async function handleCreateList() {
    if (busyList) return;
    const name = newListName.trim();
    if (!name) {
      setNewListNameError(
        t('admin.menuItemViews.newNameRequired', { defaultValue: 'Podaj nazwę' }),
      );
      return;
    }
    // Duplicate-name guard: case-insensitive, trimmed. Sprawdza ALL widoków list
    // (łącznie z domyślnym) — nowy layout nie może kolidować z żadną istniejącą nazwą.
    const nameLower = name.toLowerCase();
    const hasDuplicate = allListViews.some((v) => v.name.trim().toLowerCase() === nameLower);
    if (hasDuplicate) {
      setNewListNameError(
        t('admin.menuItemViews.duplicateName', {
          defaultValue: 'Widok o tej nazwie już istnieje.',
        }),
      );
      return;
    }
    // Chip = duplikuj ze źródła. Brak chipa = tworzenie od zera.
    let action: unknown;
    if (listDuplicateChip !== undefined) {
      if (!onDuplicateList) return;
      action = onDuplicateList(listDuplicateChip, name);
    } else {
      if (!onCreateList) return;
      action = onCreateList(name);
    }
    // Jak przy formularzu — spinner do końca POST-a i refetchu definicji.
    setBusyList(true);
    try {
      await Promise.resolve(action);
    } finally {
      setBusyList(false);
      setShowNewListInput(false);
      setNewListName('');
      setNewListNameError('');
      setListDuplicateChip(undefined);
    }
  }

  /**
   * Kliknięcie „Duplikuj" na wierszu formularza:
   * otwiera input create z chipem „kopia od: <nazwa>".
   */
  function handleDuplicate(slug: string | null) {
    if (!onDuplicateForm) return;
    setFormDuplicateChip(slug);
    setShowNewInput(true);
    setNewName('');
    setNewNameError('');
  }

  /**
   * Kliknięcie „Duplikuj" na wierszu listy:
   * otwiera input create z chipem „kopia od: <nazwa>".
   */
  function handleDuplicateList(slug: string | null) {
    if (!onDuplicateList) return;
    setListDuplicateChip(slug);
    setShowNewListInput(true);
    setNewListName('');
    setNewListNameError('');
  }

  /**
   * Zwija input formularza bez zapisu: czyści wpisaną nazwę, błąd i chip „kopia od".
   * Używany przy Escape oraz blur (klik poza input). Input jest afordancją przejściową:
   * albo otwarty+sfokusowany, albo całkowicie zniknięty — nigdy nie wisi nieaktywny.
   */
  function dismissNewFormInput() {
    setShowNewInput(false);
    setNewName('');
    setNewNameError('');
    setFormDuplicateChip(undefined);
  }

  /** Analogia dla inputu listy. */
  function dismissNewListInput() {
    setShowNewListInput(false);
    setNewListName('');
    setNewListNameError('');
    setListDuplicateChip(undefined);
  }

  /**
   * Inline rename nazwanego layoutu (ołówek na wierszu, 2026-07-03): stan edycji
   * per (typ, slug). Enter/blur = zapis (po walidacji unikalności), Escape = anuluj.
   */
  const [renameEdit, setRenameEdit] = useState<{
    type: 'form' | 'list';
    slug: string;
    name: string;
    error: string;
  } | null>(null);

  function startRename(type: 'form' | 'list', slug: string, currentName: string) {
    setRenameEdit({ type, slug, name: currentName, error: '' });
  }

  function commitRename() {
    if (!renameEdit) return;
    const name = renameEdit.name.trim();
    if (!name) {
      setRenameEdit({
        ...renameEdit,
        error: t('admin.menuItemViews.newNameRequired', { defaultValue: 'Podaj nazwę' }),
      });
      return;
    }
    // Duplicate-name guard: case-insensitive, trimmed, vs WSZYSTKIE widoki tego typu
    // (łącznie z domyślnym), z pominięciem widoku właśnie edytowanego.
    const all = renameEdit.type === 'form' ? allFormViews : allListViews;
    const nameLower = name.toLowerCase();
    const hasDuplicate = all.some(
      (v) => v.slug !== renameEdit.slug && v.name.trim().toLowerCase() === nameLower,
    );
    if (hasDuplicate) {
      setRenameEdit({
        ...renameEdit,
        error: t('admin.menuItemViews.duplicateName', {
          defaultValue: 'Widok o tej nazwie już istnieje.',
        }),
      });
      return;
    }
    const current = all.find((v) => v.slug === renameEdit.slug);
    if (current && current.name.trim() !== name) {
      if (renameEdit.type === 'form') onRenameForm?.(renameEdit.slug, name);
      else onRenameList?.(renameEdit.slug, name);
    }
    setRenameEdit(null);
  }

  /**
   * Pending-delete dla nazwanego layoutu (formularz lub lista). null = brak otwartego
   * potwierdzenia. ConfirmDialog wariant danger — zgodnie z §4.12 nigdy bez potwierdzenia.
   */
  const [pendingDeleteView, setPendingDeleteView] = useState<{
    type: 'form' | 'list';
    slug: string;
    name: string;
  } | null>(null);

  function confirmDeleteView() {
    if (!pendingDeleteView) return;
    if (pendingDeleteView.type === 'form') {
      onDeleteForm?.(pendingDeleteView.slug);
    } else {
      onDeleteList?.(pendingDeleteView.slug);
    }
    setPendingDeleteView(null);
  }

  const activeBadge = (
    <span className="ml-2 rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-semibold text-accent-700">
      {t('admin.menuItemViews.activeBadge', { defaultValue: 'aktywny' })}
    </span>
  );

  /**
   * Komórka nazwy wiersza widoku: nazwa + ołówek (inline rename, tylko nazwane
   * layouty gdy caller podał onRename*). Tryb edycji = input w miejscu nazwy
   * (Enter/blur = zapis po walidacji unikalności, Escape = anuluj).
   */
  function nameCell(
    type: 'form' | 'list',
    slug: string | null,
    name: string,
    isActive: boolean,
    canRename: boolean,
  ) {
    const isRenaming = renameEdit?.type === type && renameEdit.slug === slug;
    if (isRenaming && renameEdit) {
      return (
        <div className="min-w-0 flex-1">
          <input
            type="text"
            value={renameEdit.name}
            autoFocus
            onChange={(e) => setRenameEdit({ ...renameEdit, name: e.target.value, error: '' })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
              if (e.key === 'Escape') {
                // Esc anuluje zmianę nazwy — „obsłużone", okno nadrzędne się nie zamyka.
                e.preventDefault();
                setRenameEdit(null);
              }
            }}
            onBlur={commitRename}
            className="w-full max-w-xs rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-500"
            data-testid={`view-rename-${type}-input-${slug}`}
          />
          {renameEdit.error ? (
            <p className="mt-1 text-xs text-red-600" data-testid={`view-rename-${type}-error`}>
              {renameEdit.error}
            </p>
          ) : null}
        </div>
      );
    }
    return (
      <span className="flex flex-1 items-center gap-1.5 truncate text-sm text-text-primary">
        <span className="truncate">{name}</span>
        {canRename && slug ? (
          <button
            type="button"
            onClick={() => startRename(type, slug, name)}
            className="rounded p-0.5 text-text-muted hover:bg-bg-secondary hover:text-text-primary"
            title={t('admin.menuItemViews.renameLayout', { defaultValue: 'Zmień nazwę' })}
            aria-label={t('admin.menuItemViews.renameLayout', { defaultValue: 'Zmień nazwę' })}
            data-testid={`view-rename-${type}-open-${slug}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        ) : null}
        {isActive && activeBadge}
      </span>
    );
  }

  function formRow(slug: string | null, name: string) {
    const isActive = selectedSlug === slug;
    const isDefault = slug === null;
    return (
      <div
        key={slug ?? '__default__'}
        className={`mb-2 flex items-center gap-3 rounded-md px-3 py-2 ${
          isActive
            ? 'border-2 border-accent-400 bg-accent-50 dark:bg-accent-950/20'
            : 'border border-border-primary'
        }`}
        data-testid={`view-assign-form-row-${slug ?? 'default'}`}
      >
        {nameCell('form', slug, name, isActive, !isDefault && !!onRenameForm)}
        <div className="flex shrink-0 gap-1.5">
          {/* Wybierz — tylko dla nieaktywnych wierszy */}
          {!isActive && onAssignForm ? (
            <button
              type="button"
              onClick={() => handleAssignSlug(slug)}
              className="flex items-center gap-1.5 rounded-md border border-accent-300 px-2.5 py-1 text-xs font-medium text-accent-600 hover:bg-accent-50 dark:hover:bg-accent-950/20"
              data-testid={`view-assign-form-select-${slug ?? 'default'}`}
            >
              <CheckCircle className="h-3.5 w-3.5" />
              {t('admin.menuItemViews.select', { defaultValue: 'Wybierz' })}
            </button>
          ) : null}
          {onDuplicateForm ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                handleDuplicate(slug);
              }}
              className="flex items-center gap-1.5 rounded-md border border-border-primary px-2.5 py-1 text-xs text-text-secondary hover:bg-bg-secondary"
              title={t('admin.menuItemViews.duplicate', { defaultValue: 'Duplikuj' })}
              data-testid={`view-assign-form-duplicate-${slug ?? 'default'}`}
            >
              <Copy className="h-3.5 w-3.5" />
              {t('admin.menuItemViews.duplicate', { defaultValue: 'Duplikuj' })}
            </button>
          ) : null}
          {isDefault && onPreviewForm ? (
            <button
              type="button"
              onClick={() => onPreviewForm(slug)}
              className="flex items-center gap-1.5 rounded-md border border-border-primary px-2.5 py-1 text-xs text-text-secondary hover:bg-bg-secondary"
              data-testid="view-assign-form-preview-default"
            >
              <Eye className="h-3.5 w-3.5" />
              {t('admin.menuItemViews.preview', { defaultValue: 'Podgląd' })}
            </button>
          ) : !isDefault && onDesignForm ? (
            <button
              type="button"
              onClick={() => onDesignForm(slug)}
              className="flex items-center gap-1.5 rounded-md border border-border-primary px-2.5 py-1 text-xs text-text-secondary hover:bg-bg-secondary"
              data-testid={`view-assign-form-edit-${slug}`}
            >
              <Eye className="h-3.5 w-3.5" />
              {t('admin.menuItemViews.previewEdit', { defaultValue: 'Przeglądaj / edytuj' })}
            </button>
          ) : null}
          {/* Usuń — tylko dla nazwanych (non-default) layoutów. */}
          {!isDefault && slug && onDeleteForm ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setPendingDeleteView({ type: 'form', slug, name });
              }}
              className="flex items-center gap-1.5 rounded-md border border-border-primary px-2.5 py-1 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              title={t('admin.menuItemViews.deleteLayout', { defaultValue: 'Usuń' })}
              data-testid={`view-assign-form-delete-${slug}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t('admin.menuItemViews.deleteLayout', { defaultValue: 'Usuń' })}
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  function listRow(slug: string | null, name: string) {
    const isActive = selectedListSlug === slug;
    const isDefault = slug === null;
    return (
      <div
        key={slug ?? '__default__'}
        className={`mb-2 flex items-center gap-3 rounded-md px-3 py-2 ${
          isActive
            ? 'border-2 border-accent-400 bg-accent-50 dark:bg-accent-950/20'
            : 'border border-border-primary'
        }`}
        data-testid={`view-assign-list-row-${slug ?? 'default'}`}
      >
        {nameCell('list', slug, name, isActive, !isDefault && !!onRenameList)}
        <div className="flex shrink-0 gap-1.5">
          {/* Wybierz — tylko dla nieaktywnych wierszy */}
          {!isActive && onAssignList ? (
            <button
              type="button"
              onClick={() => handleAssignListSlug(slug)}
              className="flex items-center gap-1.5 rounded-md border border-accent-300 px-2.5 py-1 text-xs font-medium text-accent-600 hover:bg-accent-50 dark:hover:bg-accent-950/20"
              data-testid={`view-assign-list-select-${slug ?? 'default'}`}
            >
              <CheckCircle className="h-3.5 w-3.5" />
              {t('admin.menuItemViews.select', { defaultValue: 'Wybierz' })}
            </button>
          ) : null}
          {onDuplicateList ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                handleDuplicateList(slug);
              }}
              className="flex items-center gap-1.5 rounded-md border border-border-primary px-2.5 py-1 text-xs text-text-secondary hover:bg-bg-secondary"
              title={t('admin.menuItemViews.duplicate', { defaultValue: 'Duplikuj' })}
              data-testid={`view-assign-list-duplicate-${slug ?? 'default'}`}
            >
              <Copy className="h-3.5 w-3.5" />
              {t('admin.menuItemViews.duplicate', { defaultValue: 'Duplikuj' })}
            </button>
          ) : null}
          {isDefault && onPreviewList ? (
            <button
              type="button"
              onClick={() => onPreviewList(null)}
              className="flex items-center gap-1.5 rounded-md border border-border-primary px-2.5 py-1 text-xs text-text-secondary hover:bg-bg-secondary"
              data-testid="view-assign-list-preview-default"
            >
              <Eye className="h-3.5 w-3.5" />
              {t('admin.menuItemViews.preview', { defaultValue: 'Podgląd' })}
            </button>
          ) : null}
          {!isDefault && slug && onDesignList ? (
            <button
              type="button"
              onClick={() => onDesignList(slug)}
              className="flex items-center gap-1.5 rounded-md border border-border-primary px-2.5 py-1 text-xs text-text-secondary hover:bg-bg-secondary"
              data-testid={`view-assign-list-edit-${slug}`}
            >
              <Eye className="h-3.5 w-3.5" />
              {t('admin.menuItemViews.previewEdit', {
                defaultValue: 'Przeglądaj / edytuj',
              })}
            </button>
          ) : null}
          {/* Usuń — tylko dla nazwanych (non-default) widoków listy. */}
          {!isDefault && slug && onDeleteList ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setPendingDeleteView({ type: 'list', slug, name });
              }}
              className="flex items-center gap-1.5 rounded-md border border-border-primary px-2.5 py-1 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              title={t('admin.menuItemViews.deleteLayout', { defaultValue: 'Usuń' })}
              data-testid={`view-assign-list-delete-${slug}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t('admin.menuItemViews.deleteLayout', { defaultValue: 'Usuń' })}
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  const showViewSection = !!(
    entity &&
    entitySlug &&
    (onAssignForm || onDesignForm || onDesignList)
  );

  return (
    <div
      className="space-y-2 border-t border-border-secondary bg-bg-elevated px-3 py-3"
      data-testid={`menu-item-inline-editor-${item.id}`}
    >
      {/* ── Karta 1: Ustawienia ──────────────────────────────────────── */}
      <div className="rounded-lg border border-border-secondary bg-bg-secondary p-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-muted">
          {t('admin.menuItemViews.sectionSettings', { defaultValue: 'Ustawienia' })}
        </p>

        <div className="flex flex-col gap-3">
          {item.type === 'separator' ? (
            <p className="text-xs text-text-muted">
              {t(`${i18nPrefix}.properties.separatorNoFields`)}
            </p>
          ) : null}

          {item.type === 'custom_link' ? (
            <InlineField label={t(`${i18nPrefix}.properties.fieldUrl`)} required testId="props-url">
              <input
                type="text"
                value={item.url ?? ''}
                onChange={(e) => onChange({ ...item, url: e.target.value })}
                placeholder={t(`${i18nPrefix}.properties.fieldUrlPlaceholder`)}
                className="w-full rounded-md border border-border-secondary bg-bg-primary px-2.5 py-1.5 text-sm"
              />
            </InlineField>
          ) : null}

          {item.type === 'custom_link' ? (
            <InlineField
              label={t(`${i18nPrefix}.properties.fieldExternal`)}
              testId="props-external"
            >
              <label className="flex items-center gap-2 text-sm text-text-secondary">
                <input
                  type="checkbox"
                  checked={item.external ?? false}
                  onChange={(e) => onChange({ ...item, external: e.target.checked })}
                />
                {t(`${i18nPrefix}.properties.fieldExternalHint`)}
              </label>
            </InlineField>
          ) : null}

          {/* Module-type: Ikona + Etykieta + Kod modułu (slug) w jednym poziomym rzędzie. */}
          {item.type === 'module' ? (
            <>
              <div className="flex flex-wrap items-start gap-3">
                <InlineField
                  label={t(`${i18nPrefix}.properties.fieldIcon`)}
                  testId="props-icon"
                  className="flex-shrink-0"
                >
                  <IconPicker
                    value={item.iconName ?? ''}
                    onChange={(name) => onChange({ ...item, iconName: name || undefined })}
                  />
                </InlineField>

                <InlineField
                  label={t(`${i18nPrefix}.properties.fieldLabel`)}
                  testId="props-label"
                  className="min-w-[8rem] flex-1"
                >
                  <input
                    type="text"
                    value={item.label ?? ''}
                    onChange={(e) => onChange({ ...item, label: e.target.value })}
                    placeholder={item.label ?? '—'}
                    className="w-full rounded-md border border-border-secondary bg-bg-primary px-2.5 py-1.5 text-sm"
                  />
                </InlineField>

                <InlineField
                  label={t(`${i18nPrefix}.properties.fieldModuleCode`)}
                  testId="props-module-code"
                  className="min-w-[8rem] flex-1"
                >
                  <input
                    type="text"
                    value={item.moduleCode ?? ''}
                    readOnly
                    className="w-full cursor-not-allowed rounded-md border border-border-secondary bg-bg-tertiary px-2.5 py-1.5 text-sm text-text-muted"
                  />
                </InlineField>
              </div>

              {/* Linia pomocnicza pod rzędem (pełna szerokość): tylko podpowiedź
                  o pustej etykiecie. Odwołanie do kartoteki = badge przy etykiecie. */}
              <p className="text-[11px] text-text-muted">
                {t(`${i18nPrefix}.properties.fieldLabelHintModule`)}
              </p>
            </>
          ) : null}

          {/* Pozostałe typy (custom_link, section): Etykieta osobno, pełna szerokość. */}
          {item.type !== 'separator' && item.type !== 'module' ? (
            <InlineField
              label={t(`${i18nPrefix}.properties.fieldLabel`)}
              required={item.type === 'custom_link' || item.type === 'section'}
              hint={
                item.type === 'system_item' || item.type === 'installed_area'
                  ? t(`${i18nPrefix}.properties.fieldLabelHintModule`)
                  : undefined
              }
              testId="props-label"
            >
              <input
                type="text"
                value={item.label ?? ''}
                onChange={(e) => onChange({ ...item, label: e.target.value })}
                placeholder={item.label ?? '—'}
                className="w-full rounded-md border border-border-secondary bg-bg-primary px-2.5 py-1.5 text-sm"
              />
            </InlineField>
          ) : null}

          {/* Ikona osobno dla typów innych niż module (module ma ją w rzędzie wyżej). */}
          {item.type !== 'separator' && item.type !== 'module' ? (
            <InlineField
              label={t(`${i18nPrefix}.properties.fieldIcon`)}
              hint={t(`${i18nPrefix}.properties.fieldIconHint`)}
              testId="props-icon"
            >
              <IconPicker
                value={item.iconName ?? ''}
                onChange={(name) => onChange({ ...item, iconName: name || undefined })}
              />
            </InlineField>
          ) : null}

          {item.type === 'section' ? (
            <InlineField
              label={t(`${i18nPrefix}.properties.fieldCollapsed`)}
              testId="props-collapsed"
            >
              <label className="flex items-center gap-2 text-sm text-text-secondary">
                <input
                  type="checkbox"
                  checked={item.collapsed ?? false}
                  onChange={(e) => onChange({ ...item, collapsed: e.target.checked })}
                />
                {t(`${i18nPrefix}.properties.fieldCollapsedHint`)}
              </label>
            </InlineField>
          ) : null}

          {item.type === 'installed_area' ? (
            <InlineField
              label={t(`${i18nPrefix}.properties.fieldAreaCode`)}
              hint={t(`${i18nPrefix}.properties.installedAreaHint`)}
              testId="props-area-code"
            >
              <input
                type="text"
                value={(item as MenuItem).areaCode ?? ''}
                readOnly
                className="w-full cursor-not-allowed rounded-md border border-border-secondary bg-bg-tertiary px-2.5 py-1.5 text-sm text-text-muted"
              />
            </InlineField>
          ) : null}

          {item.type === 'system_item' ? (
            <InlineField label={t(`${i18nPrefix}.properties.fieldItemKey`)} testId="props-item-key">
              <input
                type="text"
                value={item.itemKey ?? ''}
                readOnly
                className="w-full cursor-not-allowed rounded-md border border-border-secondary bg-bg-tertiary px-2.5 py-1.5 text-sm text-text-muted"
              />
            </InlineField>
          ) : null}
        </div>
      </div>

      {/* ── Karty 2 + 3: Widoki (tylko kartoteki szablonu) ───────────── */}
      {showViewSection ? (
        <>
          {/* Karta 2: Layout formularza */}
          <div className="rounded-lg border border-border-secondary bg-bg-secondary p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-text-muted">
                <SquarePen className="h-3.5 w-3.5" />
                {t('admin.menuItemViews.sectionFormLayout', { defaultValue: 'Layout formularza' })}
              </p>
              {/* Podgląd „na żywo" formularza — tylko kartoteki własne (new_table). */}
              {onLivePreviewForm && entity?.ddlStrategy === 'new_table' ? (
                <button
                  type="button"
                  onClick={onLivePreviewForm}
                  className="inline-flex items-center gap-1 rounded-md border border-accent-300 bg-accent-50 px-2 py-1 text-xs font-medium text-accent-700 hover:bg-accent-100 dark:bg-accent-950/20 dark:text-accent-300 dark:hover:bg-accent-950/40"
                  data-testid="menu-item-live-preview-form"
                >
                  <MonitorPlay className="h-3.5 w-3.5" />
                  {t('admin.menuItemViews.livePreview', { defaultValue: 'Podgląd na żywo' })}
                </button>
              ) : null}
            </div>

            {/* Aktywny wiersz zawsze na górze. Domyślny jest aktywny gdy selectedSlug === null. */}
            {selectedSlug === null
              ? formRow(null, defaultLayoutName)
              : formViews
                  .filter((v) => v.slug === selectedSlug)
                  .map((v) => formRow(v.slug, v.name))}
            {selectedSlug !== null && formRow(null, defaultLayoutName)}
            {formViews.filter((v) => v.slug !== selectedSlug).map((v) => formRow(v.slug, v.name))}

            {onCreateForm ? (
              showNewInput ? (
                <div
                  className="mt-1 space-y-1.5"
                  onBlur={(e) => {
                    // Zwiń input, gdy fokus opuszcza CAŁY ten kontener (klik poza nim).
                    // relatedTarget pozostający wewnątrz (np. przycisk Utwórz) nie zwija.
                    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                      dismissNewFormInput();
                    }
                  }}
                >
                  {/* Chip „kopia od" — widoczny gdy user kliknął „Duplikuj" */}
                  {formDuplicateChip !== undefined ? (
                    <div className="flex items-center gap-1.5">
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                        {t('admin.menuItemViews.copyFromChip', {
                          name:
                            formDuplicateChip === null
                              ? defaultLayoutName
                              : (formViews.find((v) => v.slug === formDuplicateChip)?.name ??
                                defaultLayoutName),
                          defaultValue: `kopia od: ${
                            formDuplicateChip === null
                              ? defaultLayoutName
                              : (formViews.find((v) => v.slug === formDuplicateChip)?.name ??
                                defaultLayoutName)
                          }`,
                        })}
                      </span>
                    </div>
                  ) : null}
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={newName}
                        autoFocus
                        disabled={busyForm}
                        onChange={(e) => {
                          setNewName(e.target.value);
                          if (newNameError) setNewNameError('');
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void handleCreate();
                          if (e.key === 'Escape') {
                            // Esc zwija pole nowej pozycji — „obsłużone", okno się nie zamyka.
                            e.preventDefault();
                            dismissNewFormInput();
                          }
                        }}
                        placeholder={t('admin.menuItemViews.newPlaceholder', {
                          defaultValue: 'np. Oferta, Pełna karta',
                        })}
                        className="w-full rounded-md border border-border-primary bg-bg-secondary px-3 py-1.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent-500 disabled:opacity-60"
                        data-testid="view-assign-new-name"
                      />
                      {newNameError ? (
                        <p className="mt-1 text-xs text-red-600">{newNameError}</p>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      // preventDefault na mousedown: input nie traci fokusu zanim
                      // klik się zarejestruje, więc onBlur kontenera nie zwinie inputu
                      // przed odpaleniem handleCreate.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => void handleCreate()}
                      disabled={busyForm}
                      className="flex items-center gap-1.5 rounded-md bg-accent-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-60"
                      data-testid="view-assign-new-confirm"
                    >
                      {busyForm ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                      {busyForm
                        ? t('admin.menuItemViews.newConfirmBusy', { defaultValue: 'Tworzę…' })
                        : t('admin.menuItemViews.newConfirm', { defaultValue: 'Utwórz' })}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowNewInput(true)}
                  className="flex items-center gap-1.5 rounded-md border border-accent-300 px-3 py-1.5 text-sm font-medium text-accent-600 hover:bg-accent-50"
                  data-testid="view-assign-new-open"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {t('admin.menuItemViews.newForm', { defaultValue: 'Nowy formularz' })}
                </button>
              )
            ) : null}
          </div>

          {/* Karta 3: Widok list */}
          <div className="rounded-lg border border-border-secondary bg-bg-secondary p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-text-muted">
                <ListChecks className="h-3.5 w-3.5" />
                {t('admin.menuItemViews.sectionListView', { defaultValue: 'Widok list' })}
              </p>
              {/* Podgląd „na żywo" listy — tylko kartoteki własne (new_table). */}
              {onLivePreviewList && entity?.ddlStrategy === 'new_table' ? (
                <button
                  type="button"
                  onClick={onLivePreviewList}
                  className="inline-flex items-center gap-1 rounded-md border border-accent-300 bg-accent-50 px-2 py-1 text-xs font-medium text-accent-700 hover:bg-accent-100 dark:bg-accent-950/20 dark:text-accent-300 dark:hover:bg-accent-950/40"
                  data-testid="menu-item-live-preview-list"
                >
                  <MonitorPlay className="h-3.5 w-3.5" />
                  {t('admin.menuItemViews.livePreview', { defaultValue: 'Podgląd na żywo' })}
                </button>
              ) : null}
            </div>

            {/* Aktywny wiersz zawsze na górze. Domyślny jest aktywny gdy selectedListSlug === null. */}
            {selectedListSlug !== null &&
              listViews
                .filter((v) => v.slug === selectedListSlug)
                .map((v) => listRow(v.slug, v.name))}
            {listRow(
              null,
              listIsCustom && selectedListSlug === null ? customName : defaultLayoutName,
            )}
            {listViews
              .filter((v) => v.slug !== selectedListSlug)
              .map((v) => listRow(v.slug, v.name))}

            {/* Dodaj nowy widok listy */}
            {onCreateList ? (
              showNewListInput ? (
                <div
                  className="mt-1 space-y-1.5"
                  onBlur={(e) => {
                    // Zwiń input, gdy fokus opuszcza CAŁY ten kontener (klik poza nim).
                    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                      dismissNewListInput();
                    }
                  }}
                >
                  {/* Chip „kopia od" — widoczny gdy user kliknął „Duplikuj" */}
                  {listDuplicateChip !== undefined ? (
                    <div className="flex items-center gap-1.5">
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                        {t('admin.menuItemViews.copyFromChip', {
                          name:
                            listDuplicateChip === null
                              ? defaultLayoutName
                              : (listViews.find((v) => v.slug === listDuplicateChip)?.name ??
                                defaultLayoutName),
                          defaultValue: `kopia od: ${
                            listDuplicateChip === null
                              ? defaultLayoutName
                              : (listViews.find((v) => v.slug === listDuplicateChip)?.name ??
                                defaultLayoutName)
                          }`,
                        })}
                      </span>
                    </div>
                  ) : null}
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={newListName}
                        autoFocus
                        disabled={busyList}
                        onChange={(e) => {
                          setNewListName(e.target.value);
                          if (newListNameError) setNewListNameError('');
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void handleCreateList();
                          if (e.key === 'Escape') {
                            // Esc zwija pole nowej pozycji — „obsłużone", okno się nie zamyka.
                            e.preventDefault();
                            dismissNewListInput();
                          }
                        }}
                        placeholder={t('admin.menuItemViews.newListPlaceholder', {
                          defaultValue: 'np. Skrócona, Pełna tabela',
                        })}
                        className="w-full rounded-md border border-border-primary bg-bg-secondary px-3 py-1.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent-500 disabled:opacity-60"
                        data-testid="view-assign-list-new-name"
                      />
                      {newListNameError ? (
                        <p className="mt-1 text-xs text-red-600">{newListNameError}</p>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      // preventDefault na mousedown: input nie traci fokusu zanim
                      // klik się zarejestruje, więc onBlur kontenera nie zwinie inputu
                      // przed odpaleniem handleCreateList.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => void handleCreateList()}
                      disabled={busyList}
                      className="flex items-center gap-1.5 rounded-md bg-accent-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-60"
                      data-testid="view-assign-list-new-confirm"
                    >
                      {busyList ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                      {busyList
                        ? t('admin.menuItemViews.newConfirmBusy', { defaultValue: 'Tworzę…' })
                        : t('admin.menuItemViews.newConfirm', { defaultValue: 'Utwórz' })}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowNewListInput(true)}
                  className="flex items-center gap-1.5 rounded-md border border-accent-300 px-3 py-1.5 text-sm font-medium text-accent-600 hover:bg-accent-50"
                  data-testid="view-assign-list-new-open"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {t('admin.menuItemViews.newList', { defaultValue: 'Nowy widok listy' })}
                </button>
              )
            ) : null}
          </div>

          {/* Karta 4: Filtry stałe — bramka: pozycja musi mieć entity (ta sama co widoki) */}
          <div className="rounded-lg border border-border-secondary bg-bg-secondary p-4">
            <p className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-text-muted">
              <Filter className="h-3.5 w-3.5" />
              {t('admin.menuItemViews.sectionBaseFilters', { defaultValue: 'Filtry stałe' })}
            </p>

            {/* Zakładki Warunki / Sortowanie */}
            <div className="mb-3 flex gap-1 border-b border-border-secondary pb-2">
              <button
                type="button"
                onClick={() => setBaseFiltersTab('conditions')}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  baseFiltersTab === 'conditions'
                    ? 'bg-accent-100 text-accent-700 dark:bg-accent-900/40 dark:text-accent-300'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
                data-testid="base-filters-tab-conditions"
              >
                {t('admin.menuItemViews.baseFiltersTabConditions', { defaultValue: 'Warunki' })}
              </button>
              <button
                type="button"
                onClick={() => setBaseFiltersTab('sort')}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  baseFiltersTab === 'sort'
                    ? 'bg-accent-100 text-accent-700 dark:bg-accent-900/40 dark:text-accent-300'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
                data-testid="base-filters-tab-sort"
              >
                {t('admin.menuItemViews.baseFiltersTabSort', { defaultValue: 'Sortowanie' })}
              </button>
            </div>

            {baseFiltersTab === 'conditions' ? (
              <BaseFiltersEditor
                value={
                  (item.type === 'module' ? (item as MenuItem).view?.baseFilters : undefined) ?? []
                }
                onChange={(next: FilterConfig[]) => {
                  const menuItem = item as MenuItem;
                  onChange({
                    ...item,
                    view: {
                      ...menuItem.view,
                      baseFilters: next.length > 0 ? next : undefined,
                    },
                  } as MenuItem);
                }}
                fields={filterFields}
                documentClass={documentTypeNarrowingClass}
                scope={documentTypeNarrowingClass ? documentTypePickerScope : undefined}
                showCurrentUserButton={baseFiltersApplyAtRuntime}
              />
            ) : (
              <BaseSortEditor
                value={
                  (item.type === 'module' ? (item as MenuItem).view?.baseSort : undefined) ?? []
                }
                onChange={(next: SortConfig[]) => {
                  const menuItem = item as MenuItem;
                  onChange({
                    ...item,
                    view: {
                      ...menuItem.view,
                      baseSort: next.length > 0 ? next : undefined,
                    },
                  } as MenuItem);
                }}
                fields={filterFields}
              />
            )}
          </div>
        </>
      ) : null}

      {/* Potwierdzenie usunięcia nazwanego layoutu (formularz lub lista) — §4.12 */}
      <ConfirmDialog
        open={pendingDeleteView !== null}
        variant="danger"
        title={t('admin.menuItemViews.deleteLayoutTitle', { defaultValue: 'Usunąć layout?' })}
        message={
          pendingDeleteView
            ? t('admin.menuItemViews.deleteLayoutMessage', {
                name: pendingDeleteView.name,
                defaultValue: `Czy na pewno usunąć layout „${pendingDeleteView.name}"?`,
              })
            : ''
        }
        confirmLabel={t('common.delete', { defaultValue: 'Usuń' })}
        onConfirm={confirmDeleteView}
        onClose={() => setPendingDeleteView(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function InlineField({
  label,
  required,
  hint,
  children,
  testId,
  className,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
  testId?: string;
  className?: string;
}) {
  return (
    <div className={`flex flex-col gap-1${className ? ` ${className}` : ''}`} data-testid={testId}>
      <label className="text-xs font-medium text-text-secondary">
        {label}
        {required ? <span className="ml-0.5 text-red-500">*</span> : null}
      </label>
      {children}
      {hint ? <p className="text-[11px] text-text-muted">{hint}</p> : null}
    </div>
  );
}
